/**
 * Valuations Table Columns Configuration
 *
 * Defines table column structure, cell renderers, and sorting logic
 * for the Valuations list view table layout.
 */

import React from "react";
import { Link } from "react-router-dom";
import { StatusBadge, CategoryIcon } from "../../ui/StatusIndicators";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper function to get valuation category
const getValuationCategory = (valuation) => {
  const type = valuation.type?.toLowerCase() || "market-valuation";
  if (type.includes("development")) return "development";
  if (type.includes("investment")) return "investment";
  if (type.includes("market")) return "market";
  return "market";
};

const ValuationsColumns = [
  {
    key: "name",
    title: "Valuation Name",
    width: "30%",
    sortable: true,
    render: (value, valuation) => (
      <div className="flex items-center space-x-3">
        <CategoryIcon category={getValuationCategory(valuation)} size="sm" />
        <div>
          <Link
            to={`/valuation/${valuation.id}`}
            className="font-medium text-gray-900 hover:text-indigo-600"
          >
            {value || "Untitled Valuation"}
          </Link>
          {valuation.property?.address && (
            <p className="text-sm text-gray-500 truncate max-w-sm">
              {valuation.property.address}
            </p>
          )}
        </div>
      </div>
    ),
  },

  {
    key: "status",
    title: "Status",
    width: "12%",
    sortable: true,
    render: (value, valuation) => (
      <StatusBadge status={value || "draft"} type="status" />
    ),
  },

  {
    key: "type",
    title: "Type",
    width: "15%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700 capitalize">
        {value?.replace("-", " ") || "Market Valuation"}
      </span>
    ),
  },

  {
    key: "valuation.marketValue",
    title: "Value",
    width: "15%",
    sortable: true,
    render: (value, valuation) => {
      const displayValue =
        valuation.valuation?.marketValue ||
        valuation.valuation?.gdv ||
        valuation.valuation?.totalValue;
      return (
        <span className="text-sm font-medium text-gray-900">
          {formatCurrency(displayValue)}
        </span>
      );
    },
  },

  {
    key: "valuer.firm",
    title: "Valuer",
    width: "15%",
    sortable: true,
    render: (value, valuation) => (
      <span className="text-sm text-gray-700">
        {value || valuation.valuer?.surveyor || "-"}
      </span>
    ),
  },

  {
    key: "dates.reported",
    title: "Date",
    width: "13%",
    sortable: true,
    render: (value, valuation) => (
      <span className="text-sm text-gray-700">
        {formatDate(value || valuation.dates?.reported)}
      </span>
    ),
  },
];

export default ValuationsColumns;
